import Decimal from 'decimal.js'
import { DefaultTheme } from 'styled-components'
import { ReactNode } from 'react'

import { Price, round } from '@kunalabs-io/kai'

import { TableValueContentLoader } from './ContentLoaders'
import { IconClose } from 'components/Icons/IconClose'
import { IconCheck } from 'components/Icons/IconCheck'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { PositionDisplayInfo } from 'utils/userUserPositionDisplayInfos'

const HEALTH_LIMIT = 500

export const getPercentage = (priceX: Decimal, priceY: Decimal, type: 'x' | 'y') => {
  if (priceX.toNumber() + priceY.toNumber() === 0) {
    return '0'
  }
  if (type === 'x') {
    return `${((priceX.toNumber() / (priceX.toNumber() + priceY.toNumber())) * 100).toFixed(2)}%`
  }
  return `${((priceY.toNumber() / (priceX.toNumber() + priceY.toNumber())) * 100).toFixed(2)}%`
}

export const getHealthValue = (health: Decimal) => {
  if (health.mul(100).toNumber() > HEALTH_LIMIT) {
    return '> 500%'
  }
  return health.mul(100).toFixed(0) + '%'
}

export const getSupplyPoolUtilization = (utilization: number) => {
  return round(utilization * 100, 2) + '%'
}

export const getAprValue = (isLoading: boolean, status: 'active' | 'inactive' | 'closed', apr?: number): ReactNode => {
  if (status === 'closed' || status === 'inactive') {
    return '-'
  }

  if (isLoading) {
    return <TableValueContentLoader />
  }

  return apr !== undefined ? round(apr * 100, 2) + '%' : ''
}

export const getPnlValue = (isLoading: boolean, pnl?: number) => {
  if (isLoading) {
    return <TableValueContentLoader />
  }
  return pnl !== undefined ? getFormattedUsdValue(pnl) : ''
}

export const getEquityUsdValue = ({
  status,
  isLoadingUsdX,
  priceUsdX,
  equityX,
  isLoadingUsdY,
  priceUsdY,
  equityY,
  isLoadingPendingRewards,
  pendingRewardsSum,
}: {
  status: 'active' | 'inactive' | 'closed'
  isLoadingUsdX: boolean
  priceUsdX?: Price<string, string>
  equityX: Decimal
  isLoadingUsdY: boolean
  priceUsdY?: Price<string, string>
  equityY: Decimal
  isLoadingPendingRewards: boolean
  pendingRewardsSum: Decimal
}) => {
  if (status !== 'active') {
    return '-'
  }

  if (isLoadingUsdX || isLoadingUsdY || isLoadingPendingRewards) {
    return <TableValueContentLoader />
  }
  if (priceUsdX && !isLoadingUsdX && priceUsdY && !isLoadingUsdY && !isLoadingPendingRewards) {
    const equityXNum = equityX.toNumber() * priceUsdX.human.toNumber()
    const equityYNum = equityY.toNumber() * priceUsdY.human.toNumber()
    const total = equityXNum + equityYNum + pendingRewardsSum.toNumber()
    return getFormattedUsdValue(total)
  }
  return ''
}

export const getInRangeValue = (inRange: boolean | undefined, theme: DefaultTheme) => {
  if (inRange === undefined) {
    return '-'
  }
  if (inRange) {
    return <IconCheck fill={theme.colors.text.success} />
  }

  return <IconClose stroke={theme.colors.text.danger} />
}

export const getPositionRangeMin = (isReversed: boolean, position: PositionDisplayInfo, decimalPlaces = 6) => {
  return isReversed
    ? position.range.end.toDecimalPlaces(decimalPlaces).toString()
    : position.range.start.toDecimalPlaces(decimalPlaces).toString()
}

export const getPositionRangeMax = (isReversed: boolean, position: PositionDisplayInfo, decimalPlaces = 6) => {
  return isReversed
    ? position.range.start.toDecimalPlaces(decimalPlaces).toString()
    : position.range.end.toDecimalPlaces(decimalPlaces).toString()
}

export const getRangeMarkerPosition = (isReversed: boolean, position: PositionDisplayInfo) => {
  const { pa: paDec, pb: pbDec } = position.position.getRange()
  const pa = paDec.human.toNumber()
  const pb = pbDec.human.toNumber()
  const p = position.pool.currentPrice().human.toNumber()

  const relative = Math.max(0, Math.min(1, (p - pa) / (pb - pa)))

  return Math.round((isReversed ? 1 - relative : relative) * 100)
}

const MIN_PERCENT = 0
const MAX_PERCENT = 100
const LOWER_DANGER_THRESHOLD = 15
const LOWER_WARNING_THRESHOLD = 30
const UPPER_WARNING_THRESHOLD = 70
const UPPER_DANGER_THRESHOLD = 85

export const getRangeBackgroundColor = (pct: number, theme: DefaultTheme) => {
  if ((pct > MIN_PERCENT && pct <= LOWER_DANGER_THRESHOLD) || (pct >= UPPER_DANGER_THRESHOLD && pct < MAX_PERCENT)) {
    return theme.colors.positionRange.danger1.background
  }
  if (
    (pct > LOWER_DANGER_THRESHOLD && pct <= LOWER_WARNING_THRESHOLD) ||
    (pct >= UPPER_WARNING_THRESHOLD && pct < UPPER_DANGER_THRESHOLD)
  ) {
    return theme.colors.positionRange.danger2.background
  }

  if (pct > LOWER_WARNING_THRESHOLD && pct < UPPER_WARNING_THRESHOLD) {
    return theme.colors.positionRange.success.background
  }

  return theme.colors.positionRange.danger.background
}

export const getRangeBorderColor = (pct: number, theme: DefaultTheme) => {
  if ((pct > MIN_PERCENT && pct <= LOWER_DANGER_THRESHOLD) || (pct >= UPPER_DANGER_THRESHOLD && pct < MAX_PERCENT)) {
    return theme.colors.positionRange.danger1.border
  }
  if (
    (pct > LOWER_DANGER_THRESHOLD && pct <= LOWER_WARNING_THRESHOLD) ||
    (pct >= UPPER_WARNING_THRESHOLD && pct < UPPER_DANGER_THRESHOLD)
  ) {
    return theme.colors.positionRange.danger2.border
  }

  if (pct > LOWER_WARNING_THRESHOLD && pct < UPPER_WARNING_THRESHOLD) {
    return theme.colors.positionRange.success.border
  }

  return theme.colors.positionRange.danger.border
}
